import { useState, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import DeviceAuthDialog from '../../../components/devices/DeviceAuthDialog';
import DeviceList from '../../../components/devices/DeviceList';
import deviceConstants from '../../../components/devices/deviceConstants';
import useBonosService from '../../../services/BonosService';
import SttError from '../../../components/error/SttError';
import type { BonoDeviceAuth } from '../../../types/models/BonoDeviceAuth';
import { PERMISSIONS } from '../../../lib/permissions';

type BonoChipType = 'COMPLETE' | 'SINGLE';

type BonoData = {
    id: number;
    name: string;
    dateend: string;
    timeend: string;
    type?: BonoChipType;
};

type DeviceAuth = {
    id?: number;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    [key: string]: unknown;
};

type BonoDevicesProps = {
    item: BonoData;
};

const formatDate = (date: Date | string): string => {
    if (date instanceof Date) {
        return date.toISOString().split('T')[0] || '';
    }
    return date || '';
};

const mapToDeviceAuth = (auth: BonoDeviceAuth): DeviceAuth => ({
    id: auth.id,
    dateini: formatDate(auth.dateini),
    dateend: formatDate(auth.dateend),
    timeini: auth.timeini,
    timeend: auth.timeend,
    weekdays: auth.weekdays,
    devices: auth.devices,
});

export default function BonoDevices({
    item,
}: BonoDevicesProps): React.ReactElement | null {
    const queryClient = useQueryClient();
    const [viewAuth, setViewAuth] = useState<DeviceAuth | undefined>();

    const bonosService = useBonosService();
    const getBonoDevicesAuthsQuery = bonosService.useGetBonoDevicesAuths(
        item.id,
        { rows: 25 },
    );

    const mappedAuths = useMemo(() => {
        if (!getBonoDevicesAuthsQuery.data?.rows) return [];
        return getBonoDevicesAuthsQuery.data.rows.map(mapToDeviceAuth);
    }, [getBonoDevicesAuthsQuery.data?.rows]);

    if (getBonoDevicesAuthsQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (getBonoDevicesAuthsQuery.isError) {
        return <SttError />;
    }

    if (!getBonoDevicesAuthsQuery.isSuccess) {
        return null;
    }

    const handleCreate = (auth: Partial<DeviceAuth>): void => {
        setViewAuth(auth as DeviceAuth);
    };

    const handleSelect = (auth: DeviceAuth): void => {
        setViewAuth(auth);
    };

    const handleSave = (): void => {
        queryClient.invalidateQueries({
            queryKey: getBonoDevicesAuthsQuery.queryKey,
        });
    };

    const handleClose = (): void => {
        setViewAuth(undefined);
    };

    return (
        <>
            <DeviceList
                item={item}
                type={deviceConstants.BONO}
                auths={mappedAuths}
                onCreate={handleCreate}
                onSelect={handleSelect}
            />

            {viewAuth && (
                <DeviceAuthDialog
                    open
                    item={viewAuth}
                    onSave={handleSave}
                    onClose={handleClose}
                    permission={PERMISSIONS.VOUCHER.MODIFY}
                />
            )}
        </>
    );
}
